import { useCallback, useEffect, useMemo, useState } from 'react';
import { Workbook } from 'exceljs';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { FNode, FNodeTypesEnum } from '@spazious/dollhouse-fsetup';
import { Translate, useTranslate } from '@spazious/dollhouse-i18n';
import * as locales from '@spazious/dollhouse-locales';
import { useGraph, useStaging } from '@spazious/dollhouse-store';
import { styled } from '@spazious/dollhouse-theme';
import { Button, ButtonProps, Flex, InputFile, Typography } from '@spazious/dollhouse-uikit';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogProps,
  Divider,
  Paper,
  useDataTable,
} from '@spazious/dollhouse-uikit';
import { traverseVisible } from '@spazious/dollhouse-utils';
import { downloadUrl } from '@spazious/dollhouse-utils';
import { useEventCallback } from '@spazious/react-hooks';

import { useDeviceInfo } from '../../hooks/index.js';
import { cleanUpInitialSparseValue } from '../../utils/index.js';
import { replaceEmptySlots } from '../../utils/replaceEmptySlots.js';
import { CloseButton } from '../CloseButton/index.js';
import { useEvents } from '../EventManager/index.js';
import { Features } from '../Features/index.js';

const CustomDataTableCell = styled(DataTableCell)`
  background-color: var(--spz-sys-color-surface);
  > div {
    min-height: 48px;
  }
`;

const CustomDataTableHeaderCell = styled(DataTableHeaderCell)`
  background-color: var(--spz-sys-color-surface);
  > div {
    min-height: 48px;
  }
`;

const CustomDataTable = styled(DataTable)`
  overflow: inherit;
`;

const CustomDialogContent = styled(DialogContent)`
  padding: 0;
`;

const CustomDialog = styled(Dialog).withConfig({
  displayName: 'CSVDialog',
})`
  background-image: green;
  ${Paper} {
    background-color: red;
  }
`;

const localesValues = Object.values(locales);

const getColumnTranslations = (column: string) => {
  const translatedColumns = [];
  for (const locale of localesValues) {
    const translation = locale[column as keyof typeof locale];
    if (translation) {
      translatedColumns.push(translation.toLowerCase());
    }
  }
  return translatedColumns;
};

const translatedfilteredColumnsMap = {
  allergies: getColumnTranslations('Allergies'),
  menu: getColumnTranslations('Menu'),
  guest: getColumnTranslations('Guest'),
  selector: getColumnTranslations('Selector'),
  notes: getColumnTranslations('Notes'),
};

const SPZ_EXCEL_PASSWORD = 'SPZ_EXCEL_PROTECTED';
const SPZ_EXCEL_SHEET_NAME = 'Seating Plan';
const SPZ_EXCEL_LOCKED_COL_KEYS = ['selector'];

export interface CSVDialogProps extends DialogProps {}

export const CSVDialog: React.FC<CSVDialogProps> = ({ onClose, open, ...restProps }) => {
  const translate = useTranslate();
  const { graph } = useGraph();
  const { staging, createAction } = useStaging();
  const events = useEvents();

  const [nodes, setNodes] = useState<FNode[]>([]);

  const { isMobile } = useDeviceInfo();
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';

  const updateGraph = useCallback(
    (rows: { [key: string]: any }[]) => {
      const rowsNotUpdated: typeof rows = [];
      staging.prepare();
      rows.forEach((row) => {
        const { selector } = row;
        try {
          for (const [field, value] of Object.entries(row)) {
            if (field !== 'selector') {
              staging.add(createAction('update', selector, `meta.${field}`, value));
            }
          }
        } catch {
          rowsNotUpdated.push(row);
        }
      });
      if (rowsNotUpdated.length) {
        events.emit(
          'error',
          `Some rows were not updated: ${JSON.stringify(
            rowsNotUpdated
          )}. Please check them and try again`
        );
      } else {
        events.emit('notification', 'All rows were updated successfully');
      }
      staging.commit(CommanderActionTypesEnum.Meta);
    },
    [createAction, events, staging]
  );

  const shouldProcessColumn = useEventCallback((column: string) => {
    return Object.values(translatedfilteredColumnsMap).some((columns) =>
      columns.includes(column.toLowerCase())
    );
  });

  const getColumnInEnglish = useEventCallback((column: string) => {
    for (const [englishColumn, translatedColumns] of Object.entries(translatedfilteredColumnsMap)) {
      if (translatedColumns.includes(column.toLowerCase())) {
        return englishColumn;
      }
    }
    return column;
  });

  const processExcel = useCallback(
    (values: string[][]) => {
      // https://github.com/exceljs/exceljs/issues/100
      // excelJS returns the values with an empty/non-initizialised value always as first element, so if the first element is null, we remove it

      const [columns, ...rows] = cleanUpInitialSparseValue(values);

      const cleanedUpColumns = cleanUpInitialSparseValue(columns);
      const cleanedUpRows = rows.map((row) => {
        const cleanedUpRow = cleanUpInitialSparseValue(row);

        return replaceEmptySlots(cleanedUpRow);
      });

      const formattedRows = cleanedUpRows.map((row) =>
        row.reduce((acc, rowField, idx) => {
          if (shouldProcessColumn(cleanedUpColumns[idx])) {
            const columnInEnglish = getColumnInEnglish(cleanedUpColumns[idx]);
            acc[columnInEnglish] = rowField;
          }
          return acc;
        }, {} as { [key: string]: any })
      );
      updateGraph(formattedRows);
    },
    [getColumnInEnglish, shouldProcessColumn, updateGraph]
  );

  const readExcelFile = useCallback(
    (file: File) => {
      if (!file) return;
      const workbook = new Workbook();
      const excelFile = new FileReader();
      excelFile.readAsArrayBuffer(file);

      excelFile.onload = async () => {
        const buffer = excelFile.result;
        if (!buffer || !(buffer instanceof ArrayBuffer)) {
          return;
        }
        const wbLoaded = await workbook.xlsx.load(buffer);
        const worksheet = wbLoaded.getWorksheet(SPZ_EXCEL_SHEET_NAME);

        const values = worksheet?.getSheetValues() ?? [];
        if (values.length) {
          processExcel(values as string[][]);
        }
      };
    },
    [processExcel]
  );

  useEffect(
    () =>
      graph.subscribe(() => {
        const nodes: FNode[] = [];
        traverseVisible(graph as any, nodes.push.bind(nodes));
        setNodes(nodes);
      }),
    [graph]
  );

  const rows = useMemo<
    {
      guest: string;
      menu: string;
      allergies: string;
      furnitureName: string;
      furnitureType: string;
      notes: string;
      meta: string;
      selector: string;
    }[]
  >(
    () =>
      open
        ? nodes.reduce(
            (acc, node) =>
              node.type === FNodeTypesEnum.Furniture
                ? [
                    ...acc,
                    {
                      furnitureName: node.name ?? '',
                      furnitureType: node.furnitureType ?? '',
                      selector: node.selector ?? '',
                      guest: node.meta?.guest ?? '',
                      menu: node.meta?.menu ?? '',
                      allergies: node.meta?.allergies ?? '',
                      notes: node.meta?.notes ?? '',
                      meta: Object.entries(node.meta ?? {})
                        .reduce((arr, [key, value]) => {
                          if (
                            key !== 'guest' &&
                            key !== 'menu' &&
                            key !== 'allergies' &&
                            key !== 'notes' &&
                            value !== undefined &&
                            value !== ''
                          ) {
                            return [...arr, `${key}: ${value}`];
                          }

                          return arr;
                        }, [] as string[])
                        .join(', '),
                    },
                  ]
                : acc,
            [] as any[]
          )
        : [],
    [nodes, open]
  );
  const tableProps = useDataTable({
    columns: [
      {
        field: 'guest',
        headerName: translate('Guest'),
      },
      {
        field: 'menu',
        headerName: translate('Menu'),
      },
      {
        field: 'allergies',
        headerName: translate('Allergies'),
      },
      {
        field: 'furnitureName',
        headerName: translate('Furniture name'),
      },
      {
        field: 'furnitureType',
        headerName: translate('Furniture type'),
      },
      {
        field: 'notes',
        headerName: translate('Notes'),
      },
      {
        field: 'meta',
        headerName: translate('Additional note'),
      },
      {
        field: 'selector',
        headerName: translate('Selector'),
      },
    ],
    rows,
  });

  const columnsToRender = useMemo(
    () => tableProps.columns.filter(({ field }) => field !== 'selector'),
    [tableProps.columns]
  );

  const exportToExcelExcelJS = useCallback(async () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(SPZ_EXCEL_SHEET_NAME);

    const { columns: tableColumns, rows: tableRows } = tableProps;
    const columns = tableColumns.map(({ field, headerName }) => ({
      header: headerName ?? '',
      key: field,
      width: (headerName?.length ?? 0) < 15 ? 15 : headerName?.length,
    }));

    worksheet.columns = columns as { header: string; key: string; width: number }[];

    tableRows.forEach((rowData) => {
      worksheet.addRow(rowData);
    });

    // First, unlock all cells in the worksheet
    worksheet.eachRow({ includeEmpty: true }, (row) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.protection = { locked: false };
      });
    });

    // Second, unlock all columns in the worksheet
    worksheet.eachColumnKey((column) => {
      column.protection = { locked: false }; // Unlock all columns
    });

    // Then, lock cells in the specified column list
    SPZ_EXCEL_LOCKED_COL_KEYS.forEach((columnKey) => {
      worksheet.getColumn(columnKey).eachCell({ includeEmpty: true }, (cell) => {
        cell.protection = { locked: true }; // Lock cells in this column
        cell.style = { fill: { type: 'pattern', pattern: 'lightGray' } };
      });
    });

    // Protect the entire sheet
    worksheet.protect(SPZ_EXCEL_PASSWORD, {
      formatColumns: true,
      formatRows: true,
      selectLockedCells: true,
      selectUnlockedCells: true,
    });

    // Generate Excel file and create a Blob URL for download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);

    downloadUrl(url, `Diagram_${new Date().toJSON()}.xlsx`);
  }, [tableProps]);

  return (
    <CustomDialog
      maxWidth="md"
      open={open}
      width="100%"
      onClose={onClose}
      {...restProps}
      paperProps={{
        style: {
          backgroundColor: 'var(--spz-sys-color-surface)',
        },
      }}
    >
      <DialogHeader>
        <Typography size="small" variant="headline">
          <Translate>Export CSV</Translate>
        </Typography>
        <CloseButton onClick={(evt) => onClose?.(evt, 'any' as any)} />
      </DialogHeader>
      <Divider />
      <CustomDialogContent>
        {/* @ts-ignore */}
        <CustomDataTable {...tableProps} columns={columnsToRender}>
          <DataTableHeader cellComponent={CustomDataTableHeaderCell} />
          <DataTableBody cellComponent={CustomDataTableCell} />
        </CustomDataTable>
      </CustomDialogContent>
      <Divider />
      <DialogActions>
        <Flex gap="calc(var(--spacing-gap) * 2)" justifyContent="end">
          <Features features="graphs.setup.node.meta:editable">
            <InputFile
              disabled={!rows.length}
              label={translate('Upload')}
              size={buttonSize}
              onUpload={readExcelFile}
            />
          </Features>
          <Button
            color="primary"
            disabled={!rows.length}
            size={buttonSize}
            onClick={(evt) => {
              onClose?.(evt, 'any' as any);
              exportToExcelExcelJS();
            }}
          >
            <Translate>Download</Translate>
          </Button>
        </Flex>
      </DialogActions>
    </CustomDialog>
  );
};
